/**
 * The signed-in account's own settings, as a page: personal information,
 * documents, language and password — the same four tabs as the tenant Settings
 * modal, so "where do I change X" has one answer across roles.
 *
 * Two audiences, one implementation:
 *   - "owner" — the owner portal's Settings page. Adds a Payouts tab, because
 *     payout bank details are owner-only and are not personal information, so
 *     they keep their own home rather than being buried in another tab.
 *   - "staff" — Account & profile for admin/staff accounts, who have no payouts
 *     and whose Settings entry on the strip is the org-level Roles & access.
 *
 * i18n keys stay under `owner.settings.*` on purpose: the copy is shared, and
 * renaming the keys would orphan the rows already translated in the database.
 */
import { useMemo, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BellRing,
  Eye,
  EyeOff,
  Check,
  FileCheck,
  Globe,
  Landmark,
  Loader2,
  Lock,
  User as UserIcon,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguagePreferenceCard } from "@/components/settings/LanguagePreferenceCard";
import { BillingAddressCard } from "@/components/settings/BillingAddressCard";
import { PasswordResetCard } from "@/components/settings/PasswordResetCard";
import {
  AccountDocumentRow,
  type AccountDocument,
} from "@/components/settings/AccountDocumentRow";
import { OwnerNotificationPreferencesCard } from "@/components/settings/OwnerNotificationPreferencesCard";
import { toast } from "sonner";
import { PAGE_TITLE_CLASS } from "@/components/admin/v2/page-title";

function maskIban(raw: string): string {
  const cleaned = raw.replace(/\s+/g, "").toUpperCase();
  if (cleaned.length < 8) return raw || "—";
  return `${cleaned.slice(0, 4)}•• •••• •••• •••• •••• ${cleaned.slice(-4)}`;
}

export function AccountSettingsPanel({
  audience = "owner",
}: {
  audience?: "owner" | "staff";
}) {
  const showPayouts = audience === "owner";
  const queryClient = useQueryClient();
  const { user, loading: authLoading, refreshSession } = useAuth();
  const { t } = useLanguage();

  // ?section= deep-links straight to a tab (e.g. the Notifications page's
  // "Manage email preferences" button) without a second URL scheme to keep
  // in sync with the tab values below.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get("section") || "personal";
  const setActiveSection = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("section", value);
        return next;
      },
      { replace: true }
    );
  };

  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showCf, setShowCf] = useState(false);

  const [bankHolder, setBankHolder] = useState("");
  const [bankIban, setBankIban] = useState("");
  const [bankBic, setBankBic] = useState("");
  const [ibanEditing, setIbanEditing] = useState(false);

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingBank, setSavingBank] = useState(false);

  const { data: profileRow, isLoading: profileLoading } = useQuery({
    queryKey: ["owner-settings-profile", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("name, email, phone")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as {
        name: string | null;
        email: string;
        phone: string | null;
      } | null;
    },
  });

  const { data: ownerBankRows, isLoading: bankLoading } = useQuery({
    queryKey: ["owner-settings-bank", user?.id],
    enabled: !!user?.id && showPayouts,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("property_owners")
        .select(
          "id, property_id, bank_account_iban, bank_account_bic, bank_account_name"
        )
        .eq("user_id", user!.id);
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        property_id: number;
        bank_account_iban: string | null;
        bank_account_bic: string | null;
        bank_account_name: string | null;
      }>;
    },
  });

  // The account's own documents — typically uploaded for them by CoLiviq. Read
  // and rename only: there is no owner-facing upload flow, and inventing one
  // here would create a document type nobody reviews.
  const { data: accountDocuments, isLoading: documentsLoading } = useQuery({
    queryKey: ["account-documents", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenant_documents")
        .select(
          "id, title, file_name, file_url, status, notes, uploaded_by, user_id, created_at"
        )
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as AccountDocument[];
    },
  });

  const meta = user?.user_metadata as Record<string, unknown> | undefined;

  useEffect(() => {
    if (!user) return;
    setFullName(profileRow?.name ?? "");
    setEmail(profileRow?.email ?? user.email ?? "");
    setPhone(profileRow?.phone ?? "");
    const u =
      typeof meta?.username === "string"
        ? meta.username
        : (user.email?.split("@")[0] ?? "");
    setUsername(u);
  }, [user, profileRow, meta]);

  useEffect(() => {
    const first = ownerBankRows?.[0];
    if (!first) return;
    setBankHolder(first.bank_account_name ?? "");
    setBankIban(first.bank_account_iban ?? "");
    setBankBic(first.bank_account_bic ?? "");
  }, [ownerBankRows]);

  const pwLenOk = newPw.length >= 6;
  const confirmFilled = confirmPw.length > 0;
  const passwordsMismatch = confirmFilled && newPw !== confirmPw;
  const passwordsMatch = confirmFilled && newPw === confirmPw && pwLenOk;

  const canSavePassword = useMemo(
    () => pwLenOk && confirmPw === newPw,
    [pwLenOk, confirmPw, newPw]
  );

  const saveProfile = async () => {
    if (!user) return;
    setSavingProfile(true);
    try {
      // Still one statement. An owner exists TWICE — this account, and the
      // `lessors` record the Owners area and management invoices read — and
      // writing here used to move only the first, which is why a name corrected
      // on this screen showed up under Roles & access but not under Owners. A
      // trigger now carries name and phone across inside this same transaction.
      // Email deliberately stays put: it addresses management invoices and is
      // the fallback that links this account to its owner record, so carrying
      // it over is an admin decision. See
      // 20260816140000_account_profile_syncs_to_owner_record.sql.
      const { error: pErr } = await supabase
        .from("profiles")
        .update({
          email: email.trim(),
          name: fullName.trim() || null,
          phone: phone.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);
      if (pErr) throw pErr;

      if (email.trim().toLowerCase() !== (user.email ?? "").toLowerCase()) {
        const { error: emErr } = await supabase.auth.updateUser({
          email: email.trim(),
        });
        if (emErr) throw emErr;
      }

      await supabase.auth.updateUser({
        data: {
          username: username.trim() || undefined,
        },
      });

      // Say what actually happened. "Profile saved" over a name that did NOT
      // reach the owner record is the same silent drift this change exists to
      // end — it would just be quieter about it. Read after the write, so it
      // describes the link as it now stands. Best-effort: this is a message,
      // and failing to fetch it must not turn a successful save into an error.
      let reason: string | undefined;
      try {
        const { data: state } = await (
          supabase as unknown as {
            rpc: (
              fn: string
            ) => Promise<{ data: unknown; error: Error | null }>;
          }
        ).rpc("account_owner_sync_state");
        reason = (state as { reason?: string } | null)?.reason;
      } catch {
        reason = undefined;
      }
      if (reason === "entity_name_kept") {
        toast.success(
          t(
            "owner.settings.profileSavedEntityName",
            "Profile saved. Your company's registered name is unchanged — contact coliviq to update it."
          )
        );
      } else if (reason === "ambiguous") {
        toast.success(
          t(
            "owner.settings.profileSavedNotSynced",
            "Profile saved. Your account is linked to more than one owner record, so coliviq has to update those."
          )
        );
      } else {
        toast.success(t("owner.settings.profileSaved", "Profile saved."));
      }
      queryClient.invalidateQueries({ queryKey: ["owner-settings-profile"] });
      await refreshSession();
    } catch (e: unknown) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("owner.settings.saveFailed", "Save failed")
      );
    } finally {
      setSavingProfile(false);
    }
  };

  // Split out of saveProfile: a failed password check used to abandon the
  // profile save too, and the two now live on different tabs.
  const savePassword = async () => {
    if (!user) return;
    if (!pwLenOk) {
      toast.error(
        t(
          "owner.settings.passwordMin",
          "Password must be at least 6 characters."
        )
      );
      return;
    }
    if (newPw !== confirmPw) {
      toast.error(
        t("owner.settings.passwordMismatch", "New passwords do not match.")
      );
      return;
    }

    setSavingPassword(true);
    try {
      if (currentPw.trim()) {
        const { error: verErr } = await supabase.auth.signInWithPassword({
          email: user.email ?? email,
          password: currentPw,
        });
        if (verErr) {
          toast.error(
            t(
              "owner.settings.currentPasswordWrong",
              "Current password is incorrect."
            )
          );
          return;
        }
      }

      const { error: pwErr } = await supabase.auth.updateUser({
        password: newPw,
      });
      if (pwErr) throw pwErr;

      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      toast.success(t("owner.settings.passwordSaved", "Password updated."));
      await refreshSession();
    } catch (e: unknown) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("owner.settings.saveFailed", "Save failed")
      );
    } finally {
      setSavingPassword(false);
    }
  };

  const saveBank = async () => {
    if (!user?.id) return;
    if (!bankIban.trim()) {
      toast.error(t("owner.settings.ibanRequired", "IBAN is required."));
      return;
    }
    setSavingBank(true);
    try {
      const { error } = await supabase
        .from("property_owners")
        .update({
          bank_account_iban: bankIban.trim(),
          bank_account_bic: bankBic.trim() || null,
          bank_account_name: bankHolder.trim() || null,
        })
        .eq("user_id", user.id);
      if (error) throw error;
      toast.success(t("owner.settings.bankSaved", "Bank details saved."));
      queryClient.invalidateQueries({ queryKey: ["owner-settings-bank"] });
      setIbanEditing(false);
    } catch (e: unknown) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("owner.settings.saveFailed", "Save failed")
      );
    } finally {
      setSavingBank(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-9 w-9 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const registeredEmail = profileRow?.email ?? user.email ?? "";

  return (
    <div className="space-y-8 pb-16 max-w-4xl">
      <div className="space-y-1">
        {/* Owners get the shared owner-portal title; the staff account page
            keeps its own, larger heading. */}
        <h1
          className={
            showPayouts
              ? PAGE_TITLE_CLASS
              : "text-3xl font-semibold tracking-tight"
          }
        >
          {showPayouts
            ? t("owner.settings.title", "Settings")
            : t("admin.account.title", "Account & profile")}
        </h1>
        <p
          className={`text-muted-foreground text-sm max-w-2xl ${
            showPayouts ? "" : "sm:text-base"
          }`}
        >
          {showPayouts
            ? t(
                "owner.settings.subtitle",
                "Personal information, documents, language, password, and payouts."
              )
            : t(
                "admin.account.subtitle",
                "Your own name, documents, language and password. Roles & access covers who may reach what."
              )}
        </p>
      </div>

      <Tabs
        value={activeSection}
        onValueChange={setActiveSection}
        className="space-y-6"
      >
        <TabsList className="h-auto flex-wrap justify-start gap-2 bg-muted/40 p-1.5 rounded-2xl">
          <TabsTrigger
            value="personal"
            className="gap-2 rounded-xl data-[state=active]:bg-petrol data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            <UserIcon className="h-4 w-4" />
            {t("owner.settings.tabPersonal", "Personal information")}
          </TabsTrigger>
          <TabsTrigger
            value="documents"
            className="gap-2 rounded-xl data-[state=active]:bg-petrol data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            <FileCheck className="h-4 w-4" />
            {t("owner.settings.tabDocuments", "Documents")}
          </TabsTrigger>
          <TabsTrigger
            value="language"
            className="gap-2 rounded-xl data-[state=active]:bg-petrol data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            <Globe className="h-4 w-4" />
            {t("owner.settings.tabLanguage", "Language")}
          </TabsTrigger>
          <TabsTrigger
            value="login"
            className="gap-2 rounded-xl data-[state=active]:bg-petrol data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            <Lock className="h-4 w-4" />
            {t("owner.settings.tabPasswordArea", "Password")}
          </TabsTrigger>
          {showPayouts ? (
            <TabsTrigger
              value="notifications"
              className="gap-2 rounded-xl data-[state=active]:bg-petrol data-[state=active]:text-white data-[state=active]:shadow-sm"
            >
              <BellRing className="h-4 w-4" />
              {t("owner.settings.tabNotifications", "Notifications")}
            </TabsTrigger>
          ) : null}
          {showPayouts ? (
            <TabsTrigger
              value="payouts"
              className="gap-2 rounded-xl data-[state=active]:bg-petrol data-[state=active]:text-white data-[state=active]:shadow-sm"
            >
              <Landmark className="h-4 w-4" />
              {t("owner.settings.tabPayouts", "Payouts")}
            </TabsTrigger>
          ) : null}
        </TabsList>

        {/* ── Personal information ─────────────────────────────────────────── */}
        <TabsContent value="personal" className="m-0 space-y-6">
          <Card className="rounded-2xl border-border/60 shadow-sm overflow-hidden">
            <CardHeader className="border-b bg-muted/10 pb-4">
              <CardTitle className="text-lg">
                {t("owner.settings.tabPersonal", "Personal information")}
              </CardTitle>
              <CardDescription>
                {t(
                  "owner.settings.profileDesc",
                  "Your personal account details."
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {profileLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="owner-full-name">
                        {t("owner.settings.fullName", "Full name")}
                      </Label>
                      <Input
                        id="owner-full-name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="owner-username">
                        {t("owner.settings.username", "Username")}
                      </Label>
                      <Input
                        id="owner-username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner-email">
                      {t("owner.settings.email", "Email")}
                    </Label>
                    <Input
                      id="owner-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rounded-xl"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "owner.settings.emailHint",
                        "Changing this updates your sign-in address."
                      )}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner-phone">
                      {t("owner.settings.phone", "Phone number")}
                    </Label>
                    <Input
                      id="owner-phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="rounded-xl"
                    />
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      type="button"
                      className="rounded-xl"
                      disabled={savingProfile}
                      onClick={() => void saveProfile()}
                    >
                      {savingProfile
                        ? "…"
                        : t("owner.settings.saveChanges", "Save changes")}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Who the invoice is addressed to, right under who the account is.
              Owner-only: a staff account resolves to no lessor, so for them
              this could never be anything but an empty "no owner record" card. */}
          {showPayouts ? <BillingAddressCard /> : null}
        </TabsContent>

        {/* ── Documents ────────────────────────────────────────────────────── */}
        <TabsContent value="documents" className="m-0">
          <Card className="rounded-2xl border-border/60 shadow-sm overflow-hidden">
            <CardHeader className="border-b bg-muted/10 pb-4">
              <CardTitle className="text-lg">
                {t("owner.settings.tabDocuments", "Documents")}
              </CardTitle>
              <CardDescription>
                {t(
                  "owner.settings.documentsDesc",
                  "Documents on your account. Tap the pencil to rename one."
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {documentsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : !accountDocuments || accountDocuments.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t(
                    "owner.settings.documentsEmpty",
                    "No documents yet. Anything coliviq files against your account shows up here."
                  )}
                </p>
              ) : (
                <div className="space-y-2">
                  {accountDocuments.map((doc) => (
                    <AccountDocumentRow
                      key={doc.id}
                      doc={doc}
                      onRenamed={() =>
                        queryClient.invalidateQueries({
                          queryKey: ["account-documents", user.id],
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Language ─────────────────────────────────────────────────────── */}
        <TabsContent value="language" className="m-0">
          <LanguagePreferenceCard />
        </TabsContent>

        {/* ── Login ────────────────────────────────────────────────────────── */}
        {/* Two routes to a new password, in the order people need them: change
            it when you know the current one, reset by email when you don't. */}
        <TabsContent value="login" className="m-0 space-y-6">
          <Card className="rounded-2xl border-border/60 shadow-sm overflow-hidden">
            <CardHeader className="border-b bg-muted/10 pb-4">
              <CardTitle className="text-lg">
                {t("owner.settings.changePassword", "Change password")}
              </CardTitle>
              <CardDescription>
                {t(
                  "owner.settings.changePasswordDesc",
                  "You sign in with the email above. Set a new password here."
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t("owner.settings.signInEmail", "Sign-in email")}
                </p>
                <p className="text-sm font-medium text-foreground truncate mt-0.5">
                  {registeredEmail || t("owner.settings.notSet", "Not set")}
                </p>
              </div>

              <div className="grid gap-4 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="owner-pw-cur">
                    {t("owner.settings.currentPassword", "Current password")}
                  </Label>
                  <div className="relative">
                    <Input
                      id="owner-pw-cur"
                      type={showCur ? "text" : "password"}
                      autoComplete="current-password"
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      className="rounded-xl pr-10"
                    />
                    <button
                      type="button"
                      aria-label={
                        showCur
                          ? t("owner.settings.hidePassword", "Hide password")
                          : t("owner.settings.showPassword", "Show password")
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowCur((v) => !v)}
                    >
                      {showCur ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner-pw-new">
                    {t("owner.settings.newPassword", "New password")}
                  </Label>
                  <div className="relative">
                    <Input
                      id="owner-pw-new"
                      type={showNew ? "text" : "password"}
                      autoComplete="new-password"
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      className={cn(
                        "rounded-xl pr-10",
                        newPw.length > 0 && !pwLenOk && "border-destructive"
                      )}
                    />
                    <button
                      type="button"
                      aria-label={
                        showNew
                          ? t("owner.settings.hidePassword", "Hide password")
                          : t("owner.settings.showPassword", "Show password")
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowNew((v) => !v)}
                    >
                      {showNew ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {newPw.length > 0 && !pwLenOk ? (
                    <p className="text-xs text-destructive">
                      {t(
                        "owner.settings.passwordUseSix",
                        "Use at least 6 characters."
                      )}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner-pw-cf">
                    {t("owner.settings.confirmPassword", "Confirm new")}
                  </Label>
                  <div className="relative">
                    <Input
                      id="owner-pw-cf"
                      type={showCf ? "text" : "password"}
                      autoComplete="new-password"
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      className={cn(
                        "rounded-xl pr-10",
                        passwordsMismatch && "border-destructive",
                        passwordsMatch && "border-emerald-600/50"
                      )}
                    />
                    <button
                      type="button"
                      aria-label={
                        showCf
                          ? t("owner.settings.hidePassword", "Hide password")
                          : t("owner.settings.showPassword", "Show password")
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowCf((v) => !v)}
                    >
                      {showCf ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {passwordsMismatch ? (
                    <p className="text-xs text-destructive">
                      {t(
                        "owner.settings.passwordMismatchShort",
                        "Passwords don't match."
                      )}
                    </p>
                  ) : passwordsMatch ? (
                    <p className="text-xs text-emerald-700 dark:text-emerald-500 flex items-center gap-1">
                      <Check className="h-3.5 w-3.5" aria-hidden />
                      {t("owner.settings.passwordsMatch", "Passwords match.")}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  className="rounded-xl"
                  disabled={savingPassword || !canSavePassword}
                  onClick={() => void savePassword()}
                >
                  {savingPassword
                    ? "…"
                    : t("owner.settings.updatePassword", "Update password")}
                </Button>
              </div>
            </CardContent>
          </Card>

          <PasswordResetCard email={registeredEmail} intent="alternative" />
        </TabsContent>

        {/* ── Payouts (owner only) ─────────────────────────────────────────── */}
        {showPayouts ? (
          <TabsContent value="notifications" className="m-0 space-y-6">
            <OwnerNotificationPreferencesCard />
          </TabsContent>
        ) : null}

        {showPayouts ? (
          <TabsContent value="payouts" className="m-0 space-y-6">
            <Card className="rounded-2xl border-border/60 shadow-sm overflow-hidden">
              <CardHeader className="border-b bg-muted/10 pb-4">
                <CardTitle className="text-lg">
                  {t("owner.settings.bankTitle", "Bank details")}
                </CardTitle>
                <CardDescription>
                  {t(
                    "owner.settings.bankDesc",
                    "Used by coliviq to process your revenue payouts."
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {bankLoading ? (
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto block py-6" />
                ) : ownerBankRows?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "owner.settings.noPropertiesForBank",
                      "Link a property to your account to add payout bank details."
                    )}
                  </p>
                ) : (
                  <>
                    {ownerBankRows && ownerBankRows.length > 1 ? (
                      <p className="text-xs text-muted-foreground">
                        {t(
                          "owner.settings.bankMultiHint",
                          "These bank details apply to all properties you own."
                        )}
                      </p>
                    ) : null}
                    <div className="space-y-2">
                      <Label>
                        {t(
                          "owner.settings.accountHolder",
                          "Account holder name"
                        )}
                      </Label>
                      <Input
                        value={bankHolder}
                        onChange={(e) => setBankHolder(e.target.value)}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>IBAN</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          readOnly={!ibanEditing}
                          value={ibanEditing ? bankIban : maskIban(bankIban)}
                          onChange={(e) => setBankIban(e.target.value)}
                          className="rounded-xl font-mono text-sm flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="rounded-xl shrink-0"
                          onClick={() => setIbanEditing((v) => !v)}
                        >
                          {ibanEditing
                            ? t("owner.settings.done", "Done")
                            : t("owner.settings.edit", "Edit")}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2 max-w-md">
                      <Label>BIC</Label>
                      <Input
                        value={bankBic}
                        onChange={(e) => setBankBic(e.target.value)}
                        className="rounded-xl font-mono text-sm"
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        className="rounded-xl"
                        disabled={savingBank}
                        onClick={() => void saveBank()}
                      >
                        {savingBank
                          ? "…"
                          : t("owner.settings.saveChanges", "Save changes")}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <BillingAddressCard />
          </TabsContent>
        ) : null}
      </Tabs>

      <footer className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 border-t border-border/60 text-xs text-muted-foreground">
        <span>
          © {new Date().getFullYear()} coliviq
          {showPayouts
            ? ` · ${t("owner.settings.portalFooter", "Owner portal")}`
            : ""}
        </span>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <Link
            to="/contact"
            className="hover:text-foreground underline-offset-4 hover:underline"
          >
            {t("owner.settings.helpCenter", "Help center")}
          </Link>
          <Link
            to="/privacy-policy"
            className="hover:text-foreground underline-offset-4 hover:underline"
          >
            {t("owner.settings.privacy", "Privacy")}
          </Link>
          <Link
            to="/terms-of-service"
            className="hover:text-foreground underline-offset-4 hover:underline"
          >
            {t("owner.settings.terms", "Terms")}
          </Link>
        </div>
      </footer>
    </div>
  );
}
